import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Target, Loader2, Mail, Eye, Save } from 'lucide-react';
import { useAuth } from '../../../App';
import { marketClass } from './marketBenchmarks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const KIND = {
  answer_rate_pct: 'pct', wait_avg_seconds: 'sec',
  email_median_first_reply_hours: 'hours', email_within_24h_pct: 'pct',
  voicemail_recalled_4h_pct: 'pct',
};
const fmtV = (v, kind) => {
  if (v == null) return '—';
  if (kind === 'pct') return `${v}%`;
  if (kind === 'sec') return `${Math.round(v)}s`;
  if (kind === 'hours') return v < 1 ? `${Math.round(v * 60)}min` : `${v}h`;
  return String(v);
};
const STATUS_DOT = { ok: '🟢', warn: '🟡', fail: '🔴', nd: '⚪' };

export default function CsTargetsTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cs-control/targets`, { headers: authHeaders() });
      setData(r.data);
      setEdits({});
    } catch (_) { toast.error('Errore caricamento obiettivi'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setEdit = (key, field, value) => {
    setEdits(prev => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      Object.entries(edits).forEach(([k, v]) => {
        payload[k] = {};
        ['min', 'current', 'excellence'].forEach(f => { if (v[f] !== undefined && v[f] !== '') payload[k][f] = parseFloat(v[f]); });
      });
      await axios.put(`${API}/cs-control/targets`, { targets: payload }, { headers: authHeaders() });
      toast.success('Obiettivi aggiornati');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
    setSaving(false);
  };

  const previewReport = async (period) => {
    try {
      const r = await axios.get(`${API}/cs-control/report/preview`, {
        headers: authHeaders(), params: { period }, responseType: 'text',
      });
      const w = window.open('', '_blank');
      w.document.write(r.data);
      w.document.close();
    } catch (_) { toast.error('Errore anteprima report'); }
  };

  const sendNow = async (period) => {
    if (!window.confirm(`Inviare ORA il report ${period === 'week' ? 'settimanale' : 'mensile'} ai destinatari configurati?`)) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/cs-control/report/send-test`, { period }, { headers: authHeaders() });
      const ok = (r.data.recipients || []).filter(x => x.ok).length;
      const tot = (r.data.recipients || []).length;
      if (ok === 0) toast.warning('Report generato ma invio email bloccato (ambiente di anteprima). In produzione verrà inviato regolarmente.');
      else toast.success(`Report inviato a ${ok}/${tot} destinatari`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore invio'); }
    setSending(false);
  };

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-indigo-500" /></div>;
  if (!data) return null;
  const hasEdits = Object.keys(edits).length > 0;

  return (
    <div className="space-y-4" data-testid="cs-targets-tab">
      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-900">
        <p className="font-semibold flex items-center gap-1.5"><Target className="h-4 w-4" /> Come funzionano gli obiettivi</p>
        <p className="mt-1">Ogni lunedì il sistema chiude la settimana: se l'obiettivo è stato raggiunto, quello della settimana dopo <b>si alza automaticamente</b> (+2% tasso risposta, −5% sui tempi) fino al tetto di eccellenza. Se non è stato raggiunto resta fermo — e non scende mai sotto il minimo. Il report del lunedì mostra tutto, con confronto vs settimana precedente e medie di mercato.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">🎯 Obiettivi e soglie</h3>
          {hasEdits && (
            <Button size="sm" onClick={save} disabled={saving} data-testid="cs-targets-save-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />} Salva modifiche
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Metrica</th>
                <th className="text-right px-3 py-2">Ultimi 7 giorni</th>
                <th className="text-right px-3 py-2">Obiettivo attuale</th>
                <th className="text-right px-3 py-2">Minimo</th>
                <th className="text-right px-3 py-2">Eccellenza</th>
                <th className="text-right px-4 py-2">Media mercato</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.targets).map(([key, t]) => {
                const kind = KIND[key];
                const actual = data.actuals_last_7d?.[key];
                const e = edits[key] || {};
                const editCell = (field, def) => (
                  <Input type="number" step="any" className="w-24 h-7 text-right ml-auto"
                    value={e[field] !== undefined ? e[field] : def}
                    onChange={ev => setEdit(key, field, ev.target.value)}
                    data-testid={`cs-target-${key}-${field}`} />
                );
                return (
                  <tr key={key} className="border-t border-slate-100" data-testid={`cs-target-row-${key}`}>
                    <td className="px-4 py-2 font-medium text-slate-700">{t.label}</td>
                    <td className="px-3 py-2 text-right font-bold">{fmtV(actual, kind)}</td>
                    <td className="px-3 py-2 text-right">{editCell('current', t.current)}</td>
                    <td className="px-3 py-2 text-right">{editCell('min', t.min)}</td>
                    <td className="px-3 py-2 text-right">{editCell('excellence', t.excellence)}</td>
                    <td className="px-4 py-2 text-right text-slate-500">{t.market?.label || '—'} <span className="text-[10px] text-slate-400">({t.market?.note})</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Mail className="h-4 w-4" /> Report automatici</h3>
        <p className="text-xs text-slate-500">Settimanale ogni <b>lunedì alle 8:00</b> · Mensile il <b>1° del mese alle 8:10</b> → customerservice@holidayw.eu + mas.marchese@holidayw.eu. Tutte le metriche con confronto vs periodo precedente e <b>vs medie di mercato</b>, di gruppo e per operatore.</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => previewReport('week')} data-testid="cs-report-preview-week"><Eye className="h-3.5 w-3.5 mr-1" /> Anteprima settimanale</Button>
          <Button size="sm" variant="outline" onClick={() => previewReport('month')} data-testid="cs-report-preview-month"><Eye className="h-3.5 w-3.5 mr-1" /> Anteprima mensile</Button>
          {isAdmin && (
          <>
          <Button size="sm" onClick={() => sendNow('week')} disabled={sending} data-testid="cs-report-send-now">
            {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />} Invia report settimanale ora
          </Button>
          <Button size="sm" onClick={() => sendNow('month')} disabled={sending} data-testid="cs-report-send-now-month">
            {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1" />} Invia report mensile ora
          </Button>
          </>
          )}
        </div>
      </div>

      {data.history?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700">📜 Storico settimane</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2">Settimana</th>
                {Object.values(data.targets).map(t => <th key={t.label} className="text-center px-2 py-2">{t.label.split(' ')[0]}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.history.map((h, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-600">{h.week_start}</td>
                  {Object.keys(data.targets).map(k => (
                    <td key={k} className="text-center px-2 py-2" title={`${h.actuals?.[k] ?? 'n/d'} vs obiettivo ${h.targets_before?.[k]}`}>
                      {STATUS_DOT[h.statuses?.[k] || 'nd']}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
